import { useEffect, useRef, useState } from "react";
import { getCustomisationValue, getGroupData } from "../../EditablePreview";
import VisualisedNode, { IMAGE_MODES } from "../VisualisedNode";
import './radial-progress.scss';
import { HEX_TO_RGBA } from "../../../../../../../helpers/Colours";

const RadialProgress = (props) => {
    // console.log("RadialProgress props:", props);
    const customisationVars = props.node.customisationVars;

    const progressPerc = (props.fillPerc ?? 0.7);

    const timerCustomisationTab = props.node?.config?.groupSettings?.dataLink?.split('.')[0] ?? null;
    if (timerCustomisationTab === null) {
        return null;
    }
    const settings = customisationVars?.[timerCustomisationTab] ?? null;
    if (settings === null) {
        return null;
    }

    props.node.forceProportionalScaling = true;

    // This doesn't actually create a radial pie, it create a square pie
    // We are using a div with overflow hidden and corder roundness to allow square radial and rounded / circular radial
    const getRadialSvg = () => {
        const size = (settings.radialAppearance.shape[0].size * 2 + 50) * props.previewScale;

        const cornerPointsPerc = [
            {x: size / 2, y: 0, perc: 0},
            {x: size, y: 0, perc: 0.125},
            {x: size, y: size, perc: 0.375},
            {x: 0, y: size, perc: 0.625},
            {x: 0, y: 0, perc: 0.875},
            {x: size / 2, y: 0, perc: 1},
        ]

        const perc = 1 - (props.fillPerc ?? 0.7);

        const pathPointsData = cornerPointsPerc.map((point, index) => {
            if (perc > point.perc && point.perc < 1) {
                const nextPoint = cornerPointsPerc[index + 1];
                if (perc < nextPoint.perc) {
                    const percDiff = nextPoint.perc - point.perc;
                    const percInSegment = (perc - point.perc) / percDiff;
                    const x = point.x + (nextPoint.x - point.x) * percInSegment;
                    const y = point.y + (nextPoint.y - point.y) * percInSegment;
                    return `${x},${y}`;
                } else {
                    return null;
                }
            } else {
                return `${point.x},${point.y}`;
            }
        });
        let pathPointsString = `${size / 2},${size / 2} `;
        for (let i = 0; i < pathPointsData.length; i++) {
            if (pathPointsData[i]) {
                pathPointsString += pathPointsData[i] + ' ';
            }
        }
        pathPointsString += `${size / 2},${size / 2}`;

        return (
            <svg className='radial-svg' viewBox={`0 0 ${size} ${size}`}>
                <clipPath id={'radial-mask'}>
                    <polygon points={pathPointsString} fill='#FF0000' />
                </clipPath>
            </svg>
        );
    }

    /**
     * 
     * @param {Object} obj The original object
     * @param {Object} overrideObj The object with new values to apply
     * @param {Boolean} addNewProps Only replace matching properties, or add new ones?
     * @param {Array} alternativePropNames[] An array of alternative property names to check for (if we are looking for border, but the override obj uses panelBorder: [{original: 'border', override: 'panelBorder'}])
     * @returns 
     */
    const overrideObjectProps = (obj, overrideObj, addNewProps = false, alternativePropNames = [], onlyAddNew = false) => {
        const getOverrideProp = (prop) => {
            for (let i = 0; i < alternativePropNames.length; i++) {
                if (prop === alternativePropNames[i].override) {
                    return alternativePropNames[i].original;
                }
            }
            return false;
        };
        for (let prop in overrideObj) {
            const overrideProp = getOverrideProp(prop) || prop;
            if (addNewProps || obj.hasOwnProperty(overrideProp)) {
                // console.log('Overriding prop: ', overrideProp, obj[overrideProp], ' with value: ', prop, overrideObj[prop]);
                if (!onlyAddNew) {
                    obj[overrideProp] = overrideObj[prop];
                    if (addNewProps) {
                        obj[prop] = overrideObj[prop];
                    }
                } else {
                    if (!obj.hasOwnProperty(overrideProp)) {
                        obj[overrideProp] = overrideObj[prop];
                    }
                    if (addNewProps && !obj.hasOwnProperty(prop)) {
                        obj[prop] = overrideObj[prop];
                    }
                }
            }
        }
        // console.log('Overridden object: ', obj, ' with overrideObj: ', overrideObj, ' addNewProps: ', addNewProps);
        return obj;
    }

    const addBgStyle = (styleObj, originalCustomisationData) => {
        // Using our overrideObjectProps function allows us to accept alternative property names for the style object
        const customisationData = {};
        const styleObjAlternativePropNames = [
            {original: 'bgType', override: 'type'},
            {original: 'bgColour', override: 'colour'},
            {original: 'bgOpacity', override: 'opacity'},
            {original: 'bgGradientAngle', override: 'gradientAngle'},
            {original: 'bgGradientFrom', override: 'gradientFrom'},
            {original: 'bgGradientTo', override: 'gradientTo'},
            {original: 'bgImage', override: 'image'},
        ]
        overrideObjectProps(customisationData, originalCustomisationData, true, styleObjAlternativePropNames);

        if (customisationData.bgType === 'solidColour') {
            styleObj.backgroundColor = HEX_TO_RGBA(customisationData.bgColour, customisationData.bgOpacity ? customisationData.bgOpacity / 100 : 1);
        } else if (customisationData.bgType === 'gradient') {
            styleObj.background = `linear-gradient(${customisationData.bgGradientAngle}deg, ${HEX_TO_RGBA(customisationData.bgGradientFrom, customisationData.bgOpacity ? customisationData.bgOpacity / 100 : 1)}, ${HEX_TO_RGBA(customisationData.bgGradientTo, customisationData.bgOpacity ? customisationData.bgOpacity / 100 : 1)})`;
        } else if (customisationData.bgType === 'image') {
            styleObj.backgroundImage = `url(${customisationData.bgImage.srcUrl})`;
            if (!styleObj.backgroundSize) {
                styleObj.backgroundSize = 'auto';
            }
            if (!styleObj.backgroundPosition) {
                styleObj.backgroundPosition = 'center';
            }
            if (!styleObj.backgroundRepeat) {
                styleObj.backgroundRepeat = 'repeat';
            }
        } else if (customisationData.bgType === 'none') {
            styleObj.backgroundColor = 'transparent';
        }
        return styleObj;
    }

    const cssVars = {
        "--radialSize": `${(settings.radialAppearance.shape[0].size * 2 + 50) * props.previewScale}px`,
        "--containerWidth": `${(settings.radialAppearance.shape[0].size * 2 + 150) * props.previewScale}px`,
        "--radialRoundness": `${settings.radialAppearance.shape[0].roundness / 2}%`,
        "--radialOutlineWidth": `${settings.radialAppearance.outline[0].addBorder ? settings.radialAppearance.outline[0].thickness * props.previewScale : 0}px`,
        "--radialOutlineColour": settings.radialAppearance.outline[0].colour,
    }

    const fillBgObj = {};
    overrideObjectProps(fillBgObj, settings.radialAppearance.foreground[0], true, 
        [
            {original: 'bgType', override: 'fgType'},
            {original: 'bgColour', override: 'fgColour'},
            {original: 'bgGradientAngle', override: 'fgGradientAngle'},
            {original: 'bgGradientFrom', override: 'fgGradientFrom'},
            {original: 'bgGradientTo', override: 'fgGradientTo'},
            {original: 'bgOpacity', override: 'fgOpacity'},
            {original: 'bgImage', override: 'fgImage'}
        ]
    );

    return (
        <span style={cssVars}>
            <div className={`advanced-timer-timer-radial-holder`} style={{...addBgStyle({backgroundSize:'cover', backgroundRepeat:'no-repeat'}, settings.radialAppearance.background[0])}}>
                {progressPerc < 1 &&
                    <>
                        <div className='inner'>
                            {getRadialSvg()}
                        </div>
                        <div className='inner' style={addBgStyle({clipPath: `url(#radial-mask)`, backgroundSize:'cover', backgroundRepeat:'no-repeat'}, fillBgObj)}></div>
                    </>
                }
            </div>
        </span>
    );
}
export default RadialProgress;